import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { CompetencyRow, ReportOutcome, SubjectReportView } from '../report.model';

/** Les deux colonnes triables. */
type SkillSortKey = 'code' | 'status';

/**
 * Libellé du verdict « non évaluée ».
 *
 * `REPORT.competency.notAssessed` n'existe dans aucun des trois bundles — le
 * document d'origine ne montrait que des compétences évaluées. La clé
 * applicative porte exactement le bon mot au singulier dans les trois langues ;
 * `competency.notAssessedLabel` ne conviendrait pas, c'est le pluriel de la
 * carte de synthèse (« Compétences non évaluées »).
 */
const NOT_ASSESSED_KEY = 'DIAGNOSTIC_REPORT.OUTCOME.NOTASSESSED';

/**
 * Marques combinantes, à retirer après décomposition NFD.
 *
 * `\p{Mn}` plutôt qu'un intervalle de points de code : il couvre les accents
 * latins comme les voyelles brèves de l'arabe, que le lecteur ne saisit pas
 * davantage dans une zone de recherche.
 */
const MARKS = /\p{Mn}/gu;

/** Habillage de la pastille de verdict, par issue. */
const BADGE_CLASS: Record<ReportOutcome, string> = {
  Acquired: 'badge badge--success badge--md',
  NotAcquired: 'badge badge--danger badge--md',
  NotAssessed: 'badge badge--neutral badge--md',
};

/** Une ligne du tableau, prête à afficher, à filtrer et à trier. */
interface SkillRowView {
  competencyId: number;
  /** Code du référentiel, non localisé : l'identité métier, et la clé du filtre. */
  code: string;
  /** Code court affiché — `c1` / `s1` / `م1`. */
  shortCode: string;
  /** Rang dans le référentiel : l'ordre canonique, et le départage des tris. */
  displayIndex: number;
  title: string;
  skillDescriptor: string;
  outcome: ReportOutcome;
  acquired: boolean;
  badgeClass: string;
  statusLabel: string;
  /** Texte de recherche, déjà replié et minusculé. */
  haystack: string;
}

/**
 * Section « analyse des compétences » : le tableau, sa recherche et ses tris.
 *
 * ── Des signaux, pas du DOM ────────────────────────────────────────────────
 * Le document d'origine triait en réordonnant les `<tr>` dans le `<tbody>` et
 * filtrait en posant `hidden` dessus. Ici, tri et filtres sont des signaux :
 * la liste affichée est un calcul, et le gabarit s'y conforme.
 *
 * Deux défauts de ce montage ne sont pas reconduits :
 *  - l'hydratation écrivait `data-acquired="true"/"false"` alors que le tri le
 *    relisait avec `Number()` — soit `NaN`, donc un tri par statut sans effet.
 *    L'attribut vaut `"1"` / `"0"`, et le tri lit le booléen du modèle ;
 *  - le tri sur le code lisait le numéro du code du référentiel. Les codes
 *    réels ne sont pas numérotés : on trie sur `displayIndex`, le rang de la
 *    compétence dans sa matière, qui est aussi ce que le code court affiche.
 *
 * Les égalités retombent toujours sur `displayIndex` — l'ordre du référentiel —
 * et non sur l'ordre d'affichage courant : sans cela, deux tris successifs sur
 * la même colonne ne rendraient pas la même liste.
 */
@Component({
  selector: 's2e-report-skills',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportTPipe, ReportRevealDirective],
  templateUrl: './subject-skills.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectSkillsSection {
  private readonly i18n = inject(ReportI18nService);
  private readonly translate = inject(TranslateService);

  readonly subject = input.required<SubjectReportView>();

  /** Recherche libre, filtre par code, colonne et sens du tri. */
  protected readonly query = signal('');
  protected readonly codeFilter = signal('');
  protected readonly sortKey = signal<SkillSortKey>('code');
  protected readonly ascending = signal(true);

  /* Variables des libellés de tri : la colonne est une clé, résolue par le
     moteur i18n grâce à la forme `{ k: … }`. */
  protected readonly sortCodeVars = { column: { k: 'skillTable.code' } };
  protected readonly sortStatusVars = { column: { k: 'skillTable.status' } };

  /**
   * Les trois verdicts, traduits.
   *
   * Regroupés dans un seul calcul pour que la dépendance à la langue soit
   * portée une fois : `t()` lit le signal de langue, ce que `instant()` ne fait
   * pas — sans ce voisinage, le libellé « non évaluée » resterait dans la
   * langue précédente après une bascule.
   */
  protected readonly statusLabels = computed<Record<ReportOutcome, string>>(() => ({
    Acquired: this.i18n.t('competency.acquired'),
    NotAcquired: this.i18n.t('competency.notAcquired'),
    NotAssessed: String(this.translate.instant(NOT_ASSESSED_KEY)),
  }));

  /** Toutes les compétences de la matière, dans l'ordre du référentiel. */
  protected readonly allRows = computed<SkillRowView[]>(() => {
    const labels = this.statusLabels();
    return this.subject().competencies.map((competency) =>
      this.toRow(competency, labels[competency.outcome]),
    );
  });

  /**
   * Lignes affichées.
   *
   * Filtrage d'abord, tri ensuite : trier ce qu'on va masquer coûterait pour
   * rien. `filter` rend un nouveau tableau, `sort` peut donc trier sur place
   * sans toucher au calcul amont.
   */
  protected readonly rows = computed<SkillRowView[]>(() => {
    const needle = fold(this.query()).trim();
    const code = this.codeFilter();
    const key = this.sortKey();
    const ascending = this.ascending();

    const visible = this.allRows().filter(
      (row) =>
        (!code || row.code === code) && (!needle || row.haystack.includes(needle)),
    );

    return visible.sort((a, b) => {
      const delta =
        key === 'code'
          ? a.displayIndex - b.displayIndex
          : Number(a.acquired) - Number(b.acquired);
      // Égalité : ordre du référentiel, dans les deux sens de tri.
      if (delta === 0) return a.displayIndex - b.displayIndex;
      return ascending ? delta : -delta;
    });
  });

  /** « n / total compétences » — le pluriel porte sur ce qui est affiché. */
  protected readonly countVars = computed(() => ({
    count: this.rows().length,
    total: { n: this.allRows().length },
  }));

  /** Valeur d'`aria-sort` de l'en-tête : seule la colonne triée l'annonce. */
  protected ariaSort(key: SkillSortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey() !== key) return 'none';
    return this.ascending() ? 'ascending' : 'descending';
  }

  /** Premier clic : tri croissant sur la colonne. Second clic : on inverse. */
  protected toggleSort(key: SkillSortKey): void {
    if (this.sortKey() === key) {
      this.ascending.update((ascending) => !ascending);
      return;
    }
    this.sortKey.set(key);
    this.ascending.set(true);
  }

  protected onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected onFilter(event: Event): void {
    this.codeFilter.set((event.target as HTMLSelectElement).value);
  }

  private toRow(competency: CompetencyRow, statusLabel: string): SkillRowView {
    const acquired = competency.outcome === 'Acquired';

    return {
      competencyId: competency.competencyId,
      code: competency.code,
      shortCode: competency.shortCode,
      displayIndex: competency.displayIndex,
      title: competency.title,
      skillDescriptor: competency.skillDescriptor,
      outcome: competency.outcome,
      acquired,
      badgeClass: BADGE_CLASS[competency.outcome],
      statusLabel,
      /* Ce que le lecteur a sous les yeux : le code court, le descripteur et
         le verdict. Ni le code du référentiel ni l'intitulé n'en font partie —
         ils ne sont affichés nulle part dans le rapport, et les chercher
         ferait correspondre des lignes sans que rien à l'écran ne l'explique. */
      haystack: fold(`${competency.shortCode} ${competency.skillDescriptor} ${statusLabel}`),
    };
  }
}

/**
 * Repli d'accents et passage en minuscules.
 *
 * « competence » doit trouver « compétence » : la recherche d'un tableau de
 * vingt-six lignes n'a pas à exiger la bonne diacritique.
 */
function fold(value: string): string {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(MARKS, '');
}
